import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';

import { App } from '#/app';
import { ensureWidgetStyles } from '#/lib/asset-loading';
import {
  clearMountedEmbeds,
  registerMountedEmbed,
  rerenderMountedEmbeds,
  unregisterMountedEmbed,
} from '#/lib/embed-registry';
import { ensureFontFaces } from '#/lib/fonts';
import { initGtm } from '#/lib/gtm';
import { MDC_MEASURE, startMeasure } from '#/lib/performance';
import { ensureHostPrintStyles } from '#/lib/print-styles';
import { loadRuntimeConfig } from '#/lib/runtime-config';

let CurrentApp = App;

/**
 * Defines a custom element (Web Component) called 'market-data-center' that
 * serves as an embeddable version of the Market Data and Investments Explorer
 * application.
 *
 * @remarks
 *
 * Loads tailwind styles into a Shadow DOM and renders the React application
 * within it.
 */
class MarketDataCenterEmbed extends HTMLElement {
  root: Root;
  rootContainer: ShadowRoot;
  private cleanupHostPrintStyles: (() => void) | null = null;

  constructor() {
    super();

    // build the Shadow Root
    this.rootContainer = this.attachShadow({ mode: 'open' });

    // Fonts are registered once on the owner document.
    void ensureFontFaces(this.rootContainer);

    // Apply widget styles via adoptedStyleSheets. Unlike a <style> element,
    // adopted stylesheets cannot be removed by third-party scripts that
    // manipulate the DOM (e.g., CobrowseIO session replay).
    ensureWidgetStyles(this.rootContainer);

    // React's createRoot clears the container's children on first render.
    // We must give React its own mount point so it doesn't wipe other
    // DOM nodes that live in the shadow root.
    const reactMount = document.createElement('div');
    reactMount.setAttribute('data-mdc-react-root', '');
    this.rootContainer.appendChild(reactMount);

    // create the React root inside the dedicated mount div
    this.root = createRoot(reactMount);
  }

  connectedCallback() {
    // Opens `mdc:boot`; the app root closes it on React's first commit. Only
    // the first embed on the page records it.
    startMeasure(MDC_MEASURE.boot);

    // Inject document-level print styles so `@page` margins apply and the host
    // page's chrome is hidden when the user prints/saves the detail view as a
    // PDF. Shadow-scoped print rules alone cannot do either.
    this.cleanupHostPrintStyles = ensureHostPrintStyles(this);

    registerMountedEmbed(this);
    this.renderApp();
  }

  disconnectedCallback() {
    this.cleanupHostPrintStyles?.();
    this.cleanupHostPrintStyles = null;

    unregisterMountedEmbed(this);
    this.root.unmount();
  }

  renderApp() {
    this.root.render(<CurrentApp rootContainer={this.rootContainer} />);
  }
}

function registerEmbed() {
  if (!customElements.get('market-data-center')) {
    customElements.define('market-data-center', MarketDataCenterEmbed);
  }
}

void loadRuntimeConfig().then((config) => {
  // Inject GTM into the host document (not the shadow root, where a <script>
  // would be inert). Idempotent, so multiple embeds still init GTM once.
  initGtm(config.gtmId);
  registerEmbed();
});

if (import.meta.hot) {
  import.meta.hot.accept('#/app', (nextModule) => {
    if (nextModule?.App) {
      CurrentApp = nextModule.App;
    }

    rerenderMountedEmbeds();
  });

  import.meta.hot.dispose(() => {
    clearMountedEmbeds();
  });
}
